import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getAssetServiceBaseUrl } from "../../../config/apiBase";
import ModulePage, { ModuleToolbar } from "../../../shared-ui/ModulePage";
import { EmptyState, ErrorState, Skeleton } from "../../../shared-ui/States";
import "../../../shared-ui/crane-tokens.css";

const token = () => localStorage.getItem("zTaskAuthToken") || "";

function complianceApi() {
  return `${getAssetServiceBaseUrl()}/api/compliance`;
}

async function api(path, opts = {}) {
  const t = token();
  if (!t) throw new Error("Authentication token not found.");
  const res = await fetch(`${complianceApi()}${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${t}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
}

function NavLinks() {
  return (
    <ModuleToolbar>
      <Link className="crane-btn" to="/compliance">Dashboard</Link>
      <Link className="crane-btn" to="/compliance/vehicle">Vehicle (VAHAN)</Link>
      <Link className="crane-btn" to="/compliance/licence">Licence (SARATHI)</Link>
      <Link className="crane-btn" to="/compliance/history">History</Link>
      <Link className="crane-btn" to="/compliance/vehicles">Saved vehicles</Link>
      <Link className="crane-btn" to="/compliance/licences">Saved licences</Link>
    </ModuleToolbar>
  );
}

export function ComplianceDashboard() {
  const [health, setHealth] = useState(null);
  const [lookups, setLookups] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const [h, l] = await Promise.all([
        api("/health"),
        api("/lookups?limit=10"),
      ]);
      setHealth(h);
      setLookups(l.data || []);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ModulePage
      crumbs={["Operations", "ZCompliance"]}
      title="Compliance"
      description="ULIP vehicle (VAHAN) and driving licence (SARATHI) verification — isolated module."
      actions={
        <button type="button" className="crane-btn" onClick={load} disabled={busy}>
          Refresh
        </button>
      }
    >
      <NavLinks />
      {busy && <Skeleton rows={4} />}
      {err && <ErrorState description={err} onRetry={load} />}
      {!busy && !err && (
        <>
          <div className="crane-card" style={{ marginBottom: 16, padding: 16 }}>
            <h3 className="crane-card__title">ULIP connection</h3>
            <p style={{ margin: 0, fontSize: 13 }}>
              Configured: {health?.ulipConfigured ? "yes" : "no"} · Mock:{" "}
              {health?.mock ? "yes" : "no"} · Base: {health?.baseUrl || "—"}
            </p>
          </div>
          <div className="crane-card" style={{ padding: 16 }}>
            <h3 className="crane-card__title">Recent lookups</h3>
            {lookups.length === 0 ? (
              <EmptyState title="No lookups yet" description="Run a vehicle or licence verification." />
            ) : (
              <table className="crane-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>API</th>
                    <th>Subject</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lookups.map((row) => (
                    <tr key={row.id}>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</td>
                      <td>{row.api_code}</td>
                      <td>{row.request_mask}</td>
                      <td>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </ModulePage>
  );
}

export function ComplianceVehiclePage() {
  const [vehiclenumber, setVehiclenumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setResult(null);
    setSavedMsg("");
    try {
      const data = await api("/vahan/lookup", {
        method: "POST",
        body: JSON.stringify({ vehiclenumber }),
      });
      setResult(data);
    } catch (ex) {
      setErr(ex.message || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!result?.data) return;
    setBusy(true);
    setErr("");
    try {
      await api("/vehicles", { method: "POST", body: JSON.stringify(result.data) });
      setSavedMsg("Saved to compliance vehicle registry.");
    } catch (ex) {
      setErr(ex.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModulePage
      crumbs={["Operations", "ZCompliance", "Vehicle"]}
      title="Vehicle RC (VAHAN)"
      description="Verify registration against ULIP VAHAN/01. Results stay inside ZCompliance."
    >
      <NavLinks />
      <form className="crane-card" style={{ padding: 16, marginBottom: 16 }} onSubmit={lookup}>
        <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>Vehicle number</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            className="crane-input"
            style={{ minWidth: 220 }}
            value={vehiclenumber}
            onChange={(e) => setVehiclenumber(e.target.value.toUpperCase())}
            placeholder="MH12AB1234"
            required
          />
          <button type="submit" className="crane-btn crane-btn--primary" disabled={busy}>
            {busy ? "Looking up…" : "Verify"}
          </button>
        </div>
      </form>
      {err && <ErrorState description={err} />}
      {savedMsg && <p style={{ color: "#0a7a3e", fontSize: 13 }}>{savedMsg}</p>}
      {result?.data && (
        <div className="crane-card" style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <h3 className="crane-card__title" style={{ margin: 0 }}>
              {result.data.registrationNumber || "Result"}
            </h3>
            <button type="button" className="crane-btn crane-btn--primary" onClick={save} disabled={busy}>
              Save to registry
            </button>
          </div>
          <table className="crane-table">
            <tbody>
              {[
                ["Owner", result.data.ownerName],
                ["Make", result.data.maker],
                ["Model", result.data.model],
                ["Chassis", result.data.chassisNumber],
                ["Engine", result.data.engineNumber],
                ["Fuel", result.data.fuel],
                ["Class", result.data.vehicleClass],
                ["Color", result.data.color],
                ["Fitness upto", result.data.fitnessUpto],
                ["Insurance upto", result.data.insuranceUpto],
              ].map(([k, v]) => (
                <tr key={k}>
                  <th style={{ width: 160 }}>{k}</th>
                  <td>{v || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModulePage>
  );
}

export function ComplianceLicencePage() {
  const [dlnumber, setDlnumber] = useState("");
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setResult(null);
    setSavedMsg("");
    try {
      const data = await api("/sarathi/lookup", {
        method: "POST",
        body: JSON.stringify({ dlnumber, dob }),
      });
      setResult(data);
    } catch (ex) {
      setErr(ex.message || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!result?.data) return;
    setBusy(true);
    setErr("");
    try {
      await api("/licences", {
        method: "POST",
        body: JSON.stringify({ ...result.data, dob }),
      });
      setSavedMsg("Saved to compliance licence registry.");
    } catch (ex) {
      setErr(ex.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModulePage
      crumbs={["Operations", "ZCompliance", "Licence"]}
      title="Driving licence (SARATHI)"
      description="Verify DL against ULIP SARATHI/01. Results stay inside ZCompliance."
    >
      <NavLinks />
      <form className="crane-card" style={{ padding: 16, marginBottom: 16 }} onSubmit={lookup}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>DL number</label>
            <input
              className="crane-input"
              style={{ minWidth: 220 }}
              value={dlnumber}
              onChange={(e) => setDlnumber(e.target.value.toUpperCase())}
              placeholder="GJ04 20120005008"
              required
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 6 }}>DOB</label>
            <input
              className="crane-input"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="crane-btn crane-btn--primary" disabled={busy}>
            {busy ? "Looking up…" : "Verify"}
          </button>
        </div>
      </form>
      {err && <ErrorState description={err} />}
      {savedMsg && <p style={{ color: "#0a7a3e", fontSize: 13 }}>{savedMsg}</p>}
      {result?.data && (
        <div className="crane-card" style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <h3 className="crane-card__title" style={{ margin: 0 }}>
              {result.data.fullName || result.data.dlNumber || "Result"}
            </h3>
            <button type="button" className="crane-btn crane-btn--primary" onClick={save} disabled={busy}>
              Save to registry
            </button>
          </div>
          <table className="crane-table">
            <tbody>
              {[
                ["DL number", result.data.dlNumber],
                ["Status", result.data.status],
                ["Issued", result.data.issueDate],
                ["Valid to", result.data.validTo],
                ["NT valid to", result.data.nonTransportTo],
              ].map(([k, v]) => (
                <tr key={k}>
                  <th style={{ width: 160 }}>{k}</th>
                  <td>{v || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModulePage>
  );
}

export function ComplianceHistoryPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const data = await api("/lookups?limit=100");
      setRows(data.data || []);
    } catch (e) {
      setErr(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ModulePage crumbs={["Operations", "ZCompliance", "History"]} title="Lookup history">
      <NavLinks />
      {busy && <Skeleton rows={5} />}
      {err && <ErrorState description={err} onRetry={load} />}
      {!busy && !err && rows.length === 0 && (
        <EmptyState title="No history" description="Lookups will appear here." />
      )}
      {!busy && rows.length > 0 && (
        <table className="crane-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>When</th>
              <th>API</th>
              <th>Subject</th>
              <th>Status</th>
              <th>ms</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</td>
                <td>{row.api_code}</td>
                <td>{row.request_mask}</td>
                <td>{row.status}</td>
                <td>{row.duration_ms ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModulePage>
  );
}

export function ComplianceSavedVehiclesPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await api("/vehicles");
        setRows(data.data || []);
      } catch (e) {
        setErr(e.message || "Failed");
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  return (
    <ModulePage crumbs={["Operations", "ZCompliance", "Vehicles"]} title="Saved vehicles">
      <NavLinks />
      {busy && <Skeleton rows={4} />}
      {err && <ErrorState description={err} />}
      {!busy && !err && rows.length === 0 && <EmptyState title="No saved vehicles" />}
      {rows.length > 0 && (
        <table className="crane-table">
          <thead>
            <tr>
              <th>Regn</th>
              <th>Owner</th>
              <th>Make / model</th>
              <th>Verified</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.registration_number}</td>
                <td>{r.owner_name || "—"}</td>
                <td>{[r.maker, r.model].filter(Boolean).join(" / ") || "—"}</td>
                <td>{r.last_verified_at ? new Date(r.last_verified_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModulePage>
  );
}

export function ComplianceSavedLicencesPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await api("/licences");
        setRows(data.data || []);
      } catch (e) {
        setErr(e.message || "Failed");
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  return (
    <ModulePage crumbs={["Operations", "ZCompliance", "Licences"]} title="Saved licences">
      <NavLinks />
      {busy && <Skeleton rows={4} />}
      {err && <ErrorState description={err} />}
      {!busy && !err && rows.length === 0 && <EmptyState title="No saved licences" />}
      {rows.length > 0 && (
        <table className="crane-table">
          <thead>
            <tr>
              <th>DL</th>
              <th>Name</th>
              <th>Status</th>
              <th>Verified</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.dl_number}</td>
                <td>{r.full_name || "—"}</td>
                <td>{r.status || "—"}</td>
                <td>{r.last_verified_at ? new Date(r.last_verified_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModulePage>
  );
}

/** Optional deep-link placeholder */
export function ComplianceLookupDetailRedirect() {
  const { id } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/compliance/history", { replace: true, state: { id } });
  }, [id, navigate]);
  return null;
}
